import React from "react";
import { Alert, Button, Card, EmptyState, Spinner } from "../ds";
import type { IconName } from "../ds";
import { relTime } from "../lib/format";
import type { SectionHandle } from "../lib/hooks/useSection";

// Einheitliche Hülle für jede Sektion: Titel, „Stand: vor X min“,
// „Vom Router laden“-Button, Offline-/Fehlerhinweis, Leerzustand. Sieben
// Sektionen teilen sich diese eine Komponente. Der Button fragt den Router LIVE
// ab (kostet LTE) — bewusst anders benannt als das weggefallene Seiten-Neuladen.

interface SectionCardProps<T> {
  title: React.ReactNode;
  /** Was diese Sektion tut — eine Zeile, für den Bediener. */
  description?: React.ReactNode;
  section: SectionHandle<T>;
  /** Router gerade am WS? Sonst sind alle Aktionen tot. */
  deviceOnline: boolean;
  iconName?: IconName;
  /** Zusätzliche Buttons in der Kopfzeile (links von „Vom Router laden“). */
  actions?: React.ReactNode;
  /** Wird immer gerendert, auch ohne Daten (z.B. Rollback-Banner). */
  above?: React.ReactNode;
  emptyDescription?: string;
  bodyPadding?: boolean;
  children: (data: T) => React.ReactNode;
}

export function SectionCard<T>({
  title,
  description,
  section,
  deviceOnline,
  iconName = "refresh",
  actions,
  above,
  emptyDescription = "Diese Sektion wurde noch nie abgefragt. „Vom Router laden“ holt den aktuellen Stand vom Router.",
  bodyPadding = true,
  children,
}: SectionCardProps<T>) {
  const { data, fetchedAt, loading, error, offline } = section;

  return (
    <Card
      title={title}
      subtitle={
        <span className="flex flex-wrap items-center gap-x-2">
          <span>Stand: {relTime(fetchedAt)}</span>
          {description && <span className="text-content-3">· {description}</span>}
        </span>
      }
      actions={
        <>
          {actions}
          <Button
            size="sm"
            variant="outline"
            iconName={iconName}
            loading={loading}
            disabled={!deviceOnline}
            title={deviceOnline ? "Router live abfragen" : "Router ist offline"}
            onClick={() => void section.refresh()}
          >
            Vom Router laden
          </Button>
        </>
      }
      bodyPadding={false}
    >
      <div className="flex flex-col gap-3 p-4">
        {!deviceOnline && (
          <Alert variant="neutral">
            Router offline — die Anzeige zeigt den letzten bekannten Stand. Aktionen sind
            deaktiviert, bis der Router wieder am Mgmt-Kanal hängt.
          </Alert>
        )}
        {offline && deviceOnline && (
          <Alert variant="warning">
            Router hat nicht geantwortet (offline oder Zeitüberschreitung) — angezeigt wird
            weiterhin der letzte bekannte Stand.
          </Alert>
        )}
        {error && (
          <Alert variant="danger" title="Abfrage fehlgeschlagen">
            {error}
          </Alert>
        )}
        {above}
        {loading && data === null ? (
          <div className="flex justify-center p-8">
            <Spinner />
          </div>
        ) : data === null ? (
          <EmptyState
            iconName="timer"
            title="Kein Stand vorhanden"
            description={emptyDescription}
          />
        ) : (
          <div className={bodyPadding ? "" : "-mx-4 -mb-4"}>{children(data)}</div>
        )}
      </div>
    </Card>
  );
}
